import React, { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import PhoneInput from '../components/PhoneInput';
import { FiLock, FiUnlock, FiSearch, FiSave, FiRefreshCw, FiCheck } from 'react-icons/fi';

// Standalone, no Layout, no sidebar. Passcode-gated. Only updates phone +
// whatsapp on members — nothing else is reachable from this page.

const PASS_KEY = 'phone_helper_pass';
const api = axios.create({ baseURL: process.env.REACT_APP_API_URL || '/api' });

const PhoneEntry = () => {
  const [pass, setPass] = useState(() => sessionStorage.getItem(PASS_KEY) || '');
  const [unlocked, setUnlocked] = useState(false);
  const [authError, setAuthError] = useState('');
  const [authBusy, setAuthBusy] = useState(false);

  // On mount, if we already have a passcode in sessionStorage, try it silently.
  useEffect(() => {
    if (pass && !unlocked) tryUnlock(pass, /*silent*/ true);
  }, []);

  const tryUnlock = async (code, silent = false) => {
    setAuthBusy(true); setAuthError('');
    try {
      await api.post('/phone-helper/auth', { pass: code });
      sessionStorage.setItem(PASS_KEY, code);
      setUnlocked(true);
    } catch (e) {
      if (!silent) setAuthError(e.response?.data?.error || 'Invalid code');
      sessionStorage.removeItem(PASS_KEY);
      setUnlocked(false);
    } finally { setAuthBusy(false); }
  };

  const lock = () => {
    sessionStorage.removeItem(PASS_KEY);
    setUnlocked(false);
    setPass('');
  };

  if (!unlocked) {
    return (
      <UnlockScreen
        pass={pass} setPass={setPass}
        busy={authBusy} error={authError}
        onSubmit={() => tryUnlock(pass)}
      />
    );
  }
  return <SearchScreen pass={pass} onLock={lock} />;
};

// ── Unlock screen ────────────────────────────────────────────────────────
const UnlockScreen = ({ pass, setPass, busy, error, onSubmit }) => (
  <div style={shellStyle}>
    <div style={{ ...cardStyle, maxWidth: 380, textAlign: 'center' }}>
      <img src="/logo.png" alt="" style={{ width: 64, height: 64, objectFit: 'contain', margin: '0 auto 12px' }} />
      <h2 style={{ margin: '0 0 4px', fontSize: 18 }}>Phone Number Entry</h2>
      <p style={{ margin: '0 0 18px', color: '#6b7280', fontSize: 13 }}>Enter the access code to begin</p>
      <form onSubmit={(e) => { e.preventDefault(); onSubmit(); }}>
        <div style={{ position: 'relative', marginBottom: 12 }}>
          <FiLock style={{ position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)', color: '#9ca3af' }} />
          <input
            type="password"
            inputMode="numeric"
            pattern="[0-9]*"
            autoFocus
            value={pass}
            onChange={(e) => setPass(e.target.value.replace(/\D/g, ''))}
            placeholder="• • • •"
            style={{ width: '100%', padding: '14px 14px 14px 42px', border: '1px solid #d1d5db', borderRadius: 10, fontSize: 18, textAlign: 'center', letterSpacing: 4, outline: 'none' }}
          />
        </div>
        {error && <div style={{ color: '#dc2626', fontSize: 13, marginBottom: 12 }}>{error}</div>}
        <button type="submit" disabled={busy || !pass}
          style={{ width: '100%', padding: '12px 16px', background: busy ? '#a5b4fc' : '#4f46e5', color: '#fff', border: 'none', borderRadius: 10, fontSize: 15, fontWeight: 600, cursor: busy ? 'not-allowed' : 'pointer' }}>
          <FiUnlock style={{ verticalAlign: '-2px', marginRight: 6 }} /> {busy ? 'Checking…' : 'Unlock'}
        </button>
      </form>
    </div>
  </div>
);

// ── Search + edit screen ─────────────────────────────────────────────────
const SearchScreen = ({ pass, onLock }) => {
  const [q, setQ] = useState('');
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  // editState is a map: { [memberId]: { phone, whatsapp, saving, savedAt } }
  const [editState, setEditState] = useState({});
  const debounceRef = useRef(null);

  const headers = useMemo(() => ({ 'X-Phone-Helper-Pass': pass }), [pass]);

  // Debounced search whenever the query changes.
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (q.trim().length < 1) { setRows([]); return; }
    debounceRef.current = setTimeout(async () => {
      setLoading(true); setError('');
      try {
        const r = await api.get('/phone-helper/members', { params: { q }, headers });
        const list = Array.isArray(r.data) ? r.data : [];
        setRows(list);
        // Pre-seed the per-row edit state from the server-side values.
        setEditState(prev => {
          const next = { ...prev };
          list.forEach(m => {
            if (!next[m.id]) next[m.id] = { phone: m.phone || '', whatsapp: m.whatsapp || '', saving: false, savedAt: 0 };
          });
          return next;
        });
      } catch (e) {
        if (e.response?.status === 401) { onLock(); return; }
        setError(e.response?.data?.error || 'Search failed');
      } finally { setLoading(false); }
    }, 300);
    return () => debounceRef.current && clearTimeout(debounceRef.current);
  }, [q]);

  const updateField = (id, field, value) => {
    setEditState(prev => ({ ...prev, [id]: { ...prev[id], [field]: value, savedAt: 0 } }));
  };
  const copyPhoneToWa = (id) => {
    setEditState(prev => ({ ...prev, [id]: { ...prev[id], whatsapp: prev[id]?.phone || '', savedAt: 0 } }));
  };

  const save = async (id) => {
    const state = editState[id];
    if (!state) return;
    setEditState(prev => ({ ...prev, [id]: { ...prev[id], saving: true } }));
    try {
      await api.put(`/phone-helper/members/${id}/contact`,
        { phone: state.phone || null, whatsapp: state.whatsapp || null },
        { headers });
      // Also update the displayed "Current" line so the helper sees the new values.
      setRows(prev => prev.map(r => r.id === id ? { ...r, phone: state.phone || null, whatsapp: state.whatsapp || null } : r));
      setEditState(prev => ({ ...prev, [id]: { ...prev[id], saving: false, savedAt: Date.now() } }));
    } catch (e) {
      if (e.response?.status === 401) { onLock(); return; }
      alert(e.response?.data?.error || 'Save failed');
      setEditState(prev => ({ ...prev, [id]: { ...prev[id], saving: false } }));
    }
  };

  return (
    <div style={shellStyle}>
      <div style={{ ...cardStyle, maxWidth: 760, padding: 0 }}>
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '18px 22px', borderBottom: '1px solid #f1f5f9' }}>
          <div>
            <h2 style={{ margin: 0, fontSize: 17 }}>Phone Number Entry</h2>
            <div style={{ fontSize: 12, color: '#6b7280' }}>Update phone and WhatsApp only</div>
          </div>
          <button onClick={onLock}
            style={{ padding: '8px 14px', background: '#f1f5f9', color: '#374151', border: '1px solid #e5e7eb', borderRadius: 8, cursor: 'pointer', fontSize: 13, display: 'flex', alignItems: 'center', gap: 6 }}>
            <FiLock /> Lock
          </button>
        </div>

        {/* Search */}
        <div style={{ padding: 22, borderBottom: '1px solid #f1f5f9' }}>
          <div style={{ position: 'relative' }}>
            <FiSearch style={{ position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)', color: '#9ca3af' }} />
            <input
              autoFocus
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Type a name or card number…"
              style={{ width: '100%', padding: '12px 14px 12px 42px', border: '1px solid #d1d5db', borderRadius: 10, fontSize: 14, outline: 'none' }}
            />
          </div>
          {loading && <div style={{ color: '#6b7280', fontSize: 12, marginTop: 8 }}>Searching…</div>}
          {error && <div style={{ color: '#dc2626', fontSize: 12, marginTop: 8 }}>{error}</div>}
        </div>

        {/* Results */}
        <div style={{ padding: 22, maxHeight: '60vh', overflowY: 'auto' }}>
          {q && !loading && rows.length === 0 && (
            <div style={{ color: '#9ca3af', textAlign: 'center', padding: '20px 0', fontSize: 13 }}>No members match “{q}”.</div>
          )}
          {!q && (
            <div style={{ color: '#9ca3af', textAlign: 'center', padding: '20px 0', fontSize: 13 }}>Type a name above to start.</div>
          )}
          {rows.map(r => {
            const st = editState[r.id] || { phone: r.phone || '', whatsapp: r.whatsapp || '', saving: false, savedAt: 0 };
            const recentlySaved = st.savedAt && Date.now() - st.savedAt < 3000;
            return (
              <div key={r.id} style={{ padding: '14px 0', borderBottom: '1px solid #f3f4f6' }}>
                <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 6 }}>
                  <div>
                    <strong style={{ fontSize: 14 }}>{r.name}</strong>
                    {r.card_number && <span style={{ marginLeft: 8, fontFamily: 'monospace', fontSize: 11, color: '#9ca3af' }}>{r.card_number}</span>}
                  </div>
                  {recentlySaved && (
                    <span style={{ background: '#dcfce7', color: '#16a34a', padding: '2px 8px', borderRadius: 999, fontSize: 11, fontWeight: 600 }}>
                      <FiCheck style={{ verticalAlign: '-2px' }} /> saved
                    </span>
                  )}
                </div>
                <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 10 }}>
                  📞 {r.phone || '—'} · 💬 {r.whatsapp || '—'}
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '90px 1fr', gap: '10px 12px', alignItems: 'center' }}>
                  <label style={{ fontSize: 12, color: '#374151', fontWeight: 600 }}>Phone</label>
                  <PhoneInput value={st.phone} onChange={(v) => updateField(r.id, 'phone', v)} />
                  <label style={{ fontSize: 12, color: '#374151', fontWeight: 600 }}>WhatsApp</label>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <div style={{ flex: 1 }}>
                      <PhoneInput value={st.whatsapp} onChange={(v) => updateField(r.id, 'whatsapp', v)} />
                    </div>
                    <button type="button" onClick={() => copyPhoneToWa(r.id)}
                      style={{ padding: '8px 10px', background: '#f1f5f9', color: '#374151', border: '1px solid #e5e7eb', borderRadius: 8, cursor: 'pointer', fontSize: 12, whiteSpace: 'nowrap', display: 'flex', alignItems: 'center', gap: 4 }}>
                      <FiRefreshCw /> same as phone
                    </button>
                  </div>
                </div>
                <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 12 }}>
                  <button onClick={() => save(r.id)} disabled={st.saving}
                    style={{ padding: '8px 16px', background: st.saving ? '#a5b4fc' : '#4f46e5', color: '#fff', border: 'none', borderRadius: 8, cursor: st.saving ? 'not-allowed' : 'pointer', fontSize: 13, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 6 }}>
                    <FiSave /> {st.saving ? 'Saving…' : 'Save'}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

const shellStyle = {
  minHeight: '100vh',
  display: 'flex',
  alignItems: 'flex-start',
  justifyContent: 'center',
  padding: 24,
  background: 'linear-gradient(180deg, #f8fafc 0%, #eef2ff 100%)',
};
const cardStyle = {
  width: '100%',
  background: '#fff',
  border: '1px solid #e5e7eb',
  borderRadius: 16,
  boxShadow: '0 10px 30px rgba(0,0,0,0.05)',
  padding: 28,
};

export default PhoneEntry;
